<template>
  <modal
    v-model="isModalOpen"
    :header="false"
    :footer="false"
    size="lg"
    :append-to-body="true"
    @hide="closeModal"
    class="customization-demo-modal">
    <div class="customization-demo">
      <h3 class="demo-title">Customization Demos</h3>
      <p class="demo-description">
        Show Toolkit as it appears for a district running each customization. Your own profile is
        not changed, and every demo stays on until you turn it off or close this tab.
      </p>

      <p v-if="error" class="demo-status demo-error">
        Could not check which customizations are available. Close this and try again.
      </p>

      <section
        v-for="customization of catalog"
        :key="customization.key"
        class="demo-section"
        :class="{ 'demo-section--off-page': !isOnThisPage(customization) }">
        <div class="demo-section-header">
          <div class="demo-section-copy">
            <h4 class="demo-section-title">{{ customization.title }}</h4>
            <p class="demo-section-description">{{ customization.description }}</p>
          </div>
          <select
            class="form-control demo-section-select"
            :aria-label="`Demo ${customization.title} as`"
            :disabled="fetching || !isOnThisPage(customization)"
            :value="selectedProfileFor(customization.key)"
            @change="selectProfile({ customization: customization.key, profileId: $event.target.value })">
            <option :value="NONE">None</option>
            <option
              v-for="category of customization.categories"
              :key="category.profileId"
              :value="category.profileId"
              :disabled="!isProfileAvailable(customization.key, category.profileId)">{{ categoryLabel(customization.key, category) }}</option>
          </select>
        </div>

        <p v-if="!isOnThisPage(customization)" class="demo-section-note text-muted">
          Not available on this page.
        </p>

        <a
          v-for="link of customization.demoLinks"
          :key="link.url"
          class="demo-section-link"
          :href="link.url">{{ link.label }} &rarr;</a>
      </section>

      <div class="modal-footer">
        <button
          class="btn btn-default btn-turn-off-all"
          :disabled="!isDemoActive"
          @click="turnOffAll">Turn off all demos</button>
        <button class="btn btn-primary btn-close" @click="closeModal">Close</button>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { CUSTOMIZATION_CATALOG, isCustomizationOnPage } from '@/store/customizationdemo.catalog'

const NONE = ''

export default {
  name: 'CustomizationDemoModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isModalOpen: this.show,
      catalog: CUSTOMIZATION_CATALOG,
      NONE
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        this.isModalOpen = newVal
        if (newVal) {
          this.fetchAvailability()
        }
      }
    }
  },
  computed: {
    ...mapState('customizationDemo', ['fetching', 'error']),
    ...mapGetters('customizationDemo', ['demoProfileFor', 'isProfileAvailable', 'isDemoActive'])
  },
  methods: {
    ...mapActions('customizationDemo', ['fetchAvailability', 'selectProfile', 'turnOffAll']),
    selectedProfileFor(customization) {
      return this.demoProfileFor(customization) || NONE
    },
    isOnThisPage(customization) {
      return isCustomizationOnPage(customization, this.$route.path)
    },
    categoryLabel(customization, category) {
      return this.isProfileAvailable(customization, category.profileId)
        ? category.label
        : `${category.label} (unavailable)`
    },
    closeModal() {
      this.isModalOpen = false
      this.$emit('close')
    }
  }
}
</script>

<style lang="less">
.customization-demo-modal.modal.in .modal-dialog {
  z-index: 1051 !important;
}

.customization-demo {
  padding: 20px;
}

.demo-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
}

.demo-description {
  color: #666;
  margin-bottom: 24px;
}

.demo-status {
  margin-bottom: 24px;
}

.demo-error {
  color: #a94442;
}

.demo-section {
  padding-top: 16px;
  margin-bottom: 16px;
  border-top: 1px solid #eee;
}

.demo-section-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.demo-section-copy {
  flex: 1 1 auto;
  min-width: 0;
}

// Bootstrap's .form-control is width:100%, which as a flex item eats the row and squeezes the
// copy column to nothing — the basis has to be fixed here to hold the two columns apart.
.customization-demo .demo-section-select {
  flex: 0 0 240px;
  width: 240px;
}

.demo-section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 4px;
}

.demo-section-description {
  color: #666;
  margin-bottom: 8px;
}

// One per line: a customization can point at more than one page.
.demo-section-link {
  display: block;
}

.demo-section-note {
  margin-bottom: 4px;
}

// Kept visible rather than hidden: a presenter should see the whole set of customizations from
// anywhere, and the link below says where to go to run this one.
.demo-section--off-page .demo-section-copy {
  opacity: 0.6;
}

.customization-demo .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}
</style>
